import FactCheckIcon from "@mui/icons-material/FactCheck";
import InventoryIcon from "@mui/icons-material/Inventory2";
import MovingIcon from "@mui/icons-material/SwapHoriz";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";
import warehouseCheckingApi, {
  warehouseCheckingOperatorApi,
} from "../../../../../infrastructure/api/warehouseCheckingApi";
import warehouseSlipApi, {
  warehouseSlipOperatorApi,
} from "../../../../../infrastructure/api/warehouseSlipApi";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";

const pick = (row, camel, pascal) => row?.[camel] ?? row?.[pascal];

const Kpi = ({ title, value, color = "default" }) => (
  <Paper sx={{ p: 2, height: "100%" }} elevation={0} variant="outlined">
    <Typography variant="body2" color="text.secondary">
      {title}
    </Typography>
    <Typography variant="h5" fontWeight={700} color={color === "default" ? "text.primary" : `${color}.main`}>
      {value ?? 0}
    </Typography>
  </Paper>
);

export default function WarehouseCheckingPage({ source = "Management" }) {
  const api = source === "SolarentOperator" ? warehouseCheckingOperatorApi : warehouseCheckingApi;
  const slipApi = source === "SolarentOperator" ? warehouseSlipOperatorApi : warehouseSlipApi;
  const slipsLink = `/SLN/${source}/WarehouseSlips`;

  const [tab, setTab] = useState(0);
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [inventorySearch, setInventorySearch] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const [queue, setQueue] = useState([]);
  const [movements, setMovements] = useState([]);
  const [moveType, setMoveType] = useState("");
  const [stocktakes, setStocktakes] = useState([]);
  const [stocktakeDetail, setStocktakeDetail] = useState(null);
  const [countDraft, setCountDraft] = useState({});
  const [applyVariance, setApplyVariance] = useState(true);
  const [loading, setLoading] = useState(false);

  const loadWarehouses = useCallback(async () => {
    try {
      const res = await slipApi.getWarehouses();
      const data = res?.data ?? res?.Data ?? [];
      setWarehouses(Array.isArray(data) ? data : []);
    } catch {
      /* ignore */
    }
  }, [slipApi]);

  const whParams = warehouseId ? { warehouseId: Number(warehouseId) } : {};

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getDashboard(whParams);
      if (!res?.isSuccess && !res?.IsSuccess) {
        toast.error(res?.message || res?.Message || "خطا در داشبورد");
        return;
      }
      setDashboard(res?.data ?? res?.Data);
    } catch (e) {
      toast.error(e?.message || "خطا در داشبورد");
    } finally {
      setLoading(false);
    }
  }, [api, warehouseId]);

  const loadInventory = useCallback(async () => {
    try {
      const res = await api.getInventory({
        ...whParams,
        search: inventorySearch || undefined,
        onlyZeroOrLow: onlyLow,
      });
      setInventory(res?.data ?? res?.Data ?? []);
    } catch (e) {
      toast.error(e?.message || "خطا در موجودی");
    }
  }, [api, warehouseId, inventorySearch, onlyLow]);

  const loadQueue = useCallback(async () => {
    try {
      const res = await api.getOpenControlQueue(whParams);
      setQueue(res?.data ?? res?.Data ?? []);
    } catch (e) {
      toast.error(e?.message || "خطا در صف کنترل");
    }
  }, [api, warehouseId]);

  const loadMovements = useCallback(async () => {
    try {
      const res = await api.getMovementReport({
        ...whParams,
        slipType: moveType === "" ? undefined : Number(moveType),
      });
      setMovements(res?.data ?? res?.Data ?? []);
    } catch (e) {
      toast.error(e?.message || "خطا در گزارش حرکت");
    }
  }, [api, warehouseId, moveType]);

  const loadStocktakes = useCallback(async () => {
    try {
      const res = await api.getStocktakes(whParams);
      setStocktakes(res?.data ?? res?.Data ?? []);
    } catch (e) {
      toast.error(e?.message || "خطا در انبارگردانی");
    }
  }, [api, warehouseId]);

  useEffect(() => {
    loadWarehouses();
  }, [loadWarehouses]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    if (tab === 1) loadInventory();
    if (tab === 2) loadQueue();
    if (tab === 3) loadMovements();
    if (tab === 4) loadStocktakes();
  }, [tab, loadInventory, loadQueue, loadMovements, loadStocktakes]);

  const openStocktake = async (id) => {
    try {
      const res = await api.getStocktakeDetail(id);
      if (!res?.isSuccess && !res?.IsSuccess) {
        toast.error(res?.message || res?.Message || "یافت نشد");
        return;
      }
      const detail = res?.data ?? res?.Data;
      setStocktakeDetail(detail);
      const draft = {};
      (pick(detail, "lines", "Lines") || []).forEach((line) => {
        const counted = pick(line, "countedQuantity", "CountedQuantity");
        draft[pick(line, "id", "Id")] =
          counted == null ? String(pick(line, "systemQuantity", "SystemQuantity") ?? "") : String(counted);
      });
      setCountDraft(draft);
    } catch (e) {
      toast.error(e?.message || "خطا");
    }
  };

  const startStocktake = async () => {
    try {
      const res = await api.startStocktake({
        warehouseId: warehouseId ? Number(warehouseId) : null,
      });
      if (!res?.isSuccess && !res?.IsSuccess) {
        toast.error(res?.message || res?.Message || "شروع ناموفق");
        return;
      }
      toast.success(res?.message || res?.Message || "شروع شد");
      const detail = res?.data ?? res?.Data;
      setStocktakeDetail(detail);
      loadStocktakes();
      loadDashboard();
      const draft = {};
      (pick(detail, "lines", "Lines") || []).forEach((line) => {
        draft[pick(line, "id", "Id")] = String(pick(line, "systemQuantity", "SystemQuantity") ?? "");
      });
      setCountDraft(draft);
    } catch (e) {
      toast.error(e?.message || "شروع ناموفق");
    }
  };

  const saveCounts = async () => {
    if (!stocktakeDetail) return;
    const lines = Object.entries(countDraft).map(([lineId, qty]) => ({
      lineId: Number(lineId),
      countedQuantity: Number(qty),
    }));
    try {
      const res = await api.saveStocktakeCounts({
        stocktakeId: pick(stocktakeDetail, "id", "Id"),
        lines,
      });
      if (!res?.isSuccess && !res?.IsSuccess) {
        toast.error(res?.message || res?.Message || "ذخیره ناموفق");
        return;
      }
      toast.success("ذخیره شد");
      setStocktakeDetail(res?.data ?? res?.Data);
      loadStocktakes();
    } catch (e) {
      toast.error(e?.message || "ذخیره ناموفق");
    }
  };

  const completeStocktake = async () => {
    if (!stocktakeDetail) return;
    await saveCounts();
    try {
      const res = await api.completeStocktake({
        id: pick(stocktakeDetail, "id", "Id"),
        applyVarianceToStock: applyVariance,
      });
      if (!res?.isSuccess && !res?.IsSuccess) {
        toast.error(res?.message || res?.Message || "تأیید ناموفق");
        return;
      }
      toast.success(res?.message || "تأیید شد");
      setStocktakeDetail(res?.data ?? res?.Data);
      loadStocktakes();
      loadDashboard();
      loadInventory();
    } catch (e) {
      toast.error(e?.message || "تأیید ناموفق");
    }
  };

  const d = dashboard || {};

  return (
    <Box>
      <BreadcrumbsComponent />
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h5" fontWeight={700}>
          کنترل انبار
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button component={RouterLink} to={slipsLink} variant="outlined">
            برگه‌های ورود/خروج
          </Button>
          <Button variant="contained" onClick={loadDashboard} disabled={loading}>
            بروزرسانی
          </Button>
        </Stack>
      </Stack>

      <Paper sx={{ p: 2, mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>انبار</InputLabel>
          <Select
            label="انبار"
            value={warehouseId}
            onChange={(e) => setWarehouseId(e.target.value)}
          >
            <MenuItem value="">همه انبارها</MenuItem>
            {warehouses.map((w) => (
              <MenuItem key={pick(w, "id", "Id")} value={pick(w, "id", "Id")}>
                {pick(w, "title", "Title")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={6} md={3}>
          <Kpi title="تعداد SKU" value={pick(d, "skuCount", "SkuCount")} />
        </Grid>
        <Grid item xs={6} md={3}>
          <Kpi title="موجودی کل" value={pick(d, "totalOnHand", "TotalOnHand")} color="primary" />
        </Grid>
        <Grid item xs={6} md={3}>
          <Kpi title="قابل استفاده" value={pick(d, "totalAvailable", "TotalAvailable")} color="success" />
        </Grid>
        <Grid item xs={6} md={3}>
          <Kpi title="موجودی صفر" value={pick(d, "zeroStockSkuCount", "ZeroStockSkuCount")} color="error" />
        </Grid>
        <Grid item xs={6} md={3}>
          <Kpi title="برگه باز" value={pick(d, "openSlipCount", "OpenSlipCount")} color="warning" />
        </Grid>
        <Grid item xs={6} md={3}>
          <Kpi title="در انتظار بررسی" value={pick(d, "awaitingReviewCount", "AwaitingReviewCount")} />
        </Grid>
        <Grid item xs={6} md={3}>
          <Kpi title="ورود امروز" value={pick(d, "completedInboundToday", "CompletedInboundToday")} />
        </Grid>
        <Grid item xs={6} md={3}>
          <Kpi title="خروج امروز" value={pick(d, "completedOutboundToday", "CompletedOutboundToday")} />
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable">
        <Tab icon={<FactCheckIcon />} iconPosition="start" label="خلاصه کنترل" />
        <Tab icon={<InventoryIcon />} iconPosition="start" label="موجودی" />
        <Tab icon={<PlaylistAddCheckIcon />} iconPosition="start" label="صف کنترل برگه" />
        <Tab icon={<MovingIcon />} iconPosition="start" label="حرکت کالا" />
        <Tab icon={<FactCheckIcon />} iconPosition="start" label="انبارگردانی" />
      </Tabs>

      {tab === 0 && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography fontWeight={700} mb={1}>
                موجودی بحرانی / صفر
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>کالا</TableCell>
                    <TableCell>موجود</TableCell>
                    <TableCell>قابل استفاده</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(pick(d, "lowOrZeroStock", "LowOrZeroStock") || []).map((row) => (
                    <TableRow key={pick(row, "id", "Id")}>
                      <TableCell>{pick(row, "itemTitle", "ItemTitle")}</TableCell>
                      <TableCell>{pick(row, "quantityOnHand", "QuantityOnHand")}</TableCell>
                      <TableCell>{pick(row, "quantityAvailable", "QuantityAvailable")}</TableCell>
                    </TableRow>
                  ))}
                  {!(pick(d, "lowOrZeroStock", "LowOrZeroStock") || []).length && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        موردی نیست
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography fontWeight={700} mb={1}>
                برگه‌های باز برای کنترل
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>شماره</TableCell>
                    <TableCell>نوع</TableCell>
                    <TableCell>وضعیت</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(pick(d, "openSlips", "OpenSlips") || []).slice(0, 15).map((row) => (
                    <TableRow key={pick(row, "id", "Id")}>
                      <TableCell>{pick(row, "slipNumber", "SlipNumber")}</TableCell>
                      <TableCell>{pick(row, "slipTypeTitle", "SlipTypeTitle")}</TableCell>
                      <TableCell>
                        <Chip size="small" label={pick(row, "statusTitle", "StatusTitle")} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {!(pick(d, "openSlips", "OpenSlips") || []).length && (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        صف خالی است
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <Button sx={{ mt: 1 }} component={RouterLink} to={slipsLink}>
                مدیریت برگه‌ها
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tab === 1 && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap alignItems="center">
            <TextField
              size="small"
              label="جستجوی کالا"
              value={inventorySearch}
              onChange={(e) => setInventorySearch(e.target.value)}
            />
            <FormControlLabel
              control={<Checkbox checked={onlyLow} onChange={(e) => setOnlyLow(e.target.checked)} />}
              label="فقط صفر / منفی"
            />
            <Button variant="outlined" onClick={loadInventory}>
              اعمال
            </Button>
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>انبار</TableCell>
                  <TableCell>کد</TableCell>
                  <TableCell>کالا</TableCell>
                  <TableCell>واحد</TableCell>
                  <TableCell>موجودی</TableCell>
                  <TableCell>رزرو</TableCell>
                  <TableCell>قابل استفاده</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventory.map((row) => (
                  <TableRow key={pick(row, "id", "Id")}>
                    <TableCell>{pick(row, "warehouseTitle", "WarehouseTitle")}</TableCell>
                    <TableCell>{pick(row, "skuCode", "SkuCode")}</TableCell>
                    <TableCell>{pick(row, "itemTitle", "ItemTitle")}</TableCell>
                    <TableCell>{pick(row, "unitTitle", "UnitTitle")}</TableCell>
                    <TableCell>{pick(row, "quantityOnHand", "QuantityOnHand")}</TableCell>
                    <TableCell>{pick(row, "quantityReserved", "QuantityReserved")}</TableCell>
                    <TableCell>{pick(row, "quantityAvailable", "QuantityAvailable")}</TableCell>
                  </TableRow>
                ))}
                {!inventory.length && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      موجودی ثبت نشده
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {tab === 2 && (
        <Paper sx={{ p: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            برگه‌هایی که هنوز تأیید نهایی نشده‌اند — برای اقدام به صفحه برگه‌های انبار بروید.
          </Alert>
          <Button sx={{ mb: 2 }} variant="outlined" onClick={loadQueue}>
            بروزرسانی صف
          </Button>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>شماره</TableCell>
                <TableCell>نوع</TableCell>
                <TableCell>وضعیت</TableCell>
                <TableCell>طرف</TableCell>
                <TableCell>اقلام</TableCell>
                <TableCell>مهرها</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queue.map((row) => (
                <TableRow key={pick(row, "id", "Id")}>
                  <TableCell>{pick(row, "slipNumber", "SlipNumber")}</TableCell>
                  <TableCell>{pick(row, "slipTypeTitle", "SlipTypeTitle")}</TableCell>
                  <TableCell>{pick(row, "statusTitle", "StatusTitle")}</TableCell>
                  <TableCell>{pick(row, "counterpartyName", "CounterpartyName") || "—"}</TableCell>
                  <TableCell>{pick(row, "lineCount", "LineCount")}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      {pick(row, "hasSentStamp", "HasSentStamp") && <Chip size="small" label="ارسال" color="info" />}
                      {pick(row, "hasReviewedStamp", "HasReviewedStamp") && (
                        <Chip size="small" label="بررسی" color="success" />
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
              {!queue.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    صف خالی است
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 3 && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} mb={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>نوع حرکت</InputLabel>
              <Select label="نوع حرکت" value={moveType} onChange={(e) => setMoveType(e.target.value)}>
                <MenuItem value="">همه</MenuItem>
                <MenuItem value="1">ورود</MenuItem>
                <MenuItem value="2">خروج</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" onClick={loadMovements}>
              گزارش
            </Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>تاریخ</TableCell>
                <TableCell>برگه</TableCell>
                <TableCell>نوع</TableCell>
                <TableCell>کالا</TableCell>
                <TableCell>مقدار</TableCell>
                <TableCell>طرف</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movements.map((row, idx) => (
                <TableRow key={`${pick(row, "slipNumber", "SlipNumber")}-${idx}`}>
                  <TableCell>
                    {pick(row, "slipDate", "SlipDate")
                      ? new Date(pick(row, "slipDate", "SlipDate")).toLocaleDateString("fa-IR")
                      : "—"}
                  </TableCell>
                  <TableCell>{pick(row, "slipNumber", "SlipNumber")}</TableCell>
                  <TableCell>{pick(row, "slipTypeTitle", "SlipTypeTitle")}</TableCell>
                  <TableCell>{pick(row, "itemTitle", "ItemTitle")}</TableCell>
                  <TableCell>{pick(row, "quantity", "Quantity")}</TableCell>
                  <TableCell>{pick(row, "counterpartyName", "CounterpartyName") || "—"}</TableCell>
                </TableRow>
              ))}
              {!movements.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    حرکتی ثبت نشده
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {tab === 4 && (
        <Paper sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
            <Button variant="contained" onClick={startStocktake}>
              شروع انبارگردانی جدید
            </Button>
            <Button variant="outlined" onClick={loadStocktakes}>
              بروزرسانی لیست
            </Button>
          </Stack>
          <Alert severity="info" sx={{ mb: 2 }}>
            شمارش فیزیکی را ثبت کنید؛ در تأیید نهایی می‌توانید مغایرت را روی موجودی سیستم اعمال کنید.
          </Alert>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>عنوان</TableCell>
                <TableCell>انبار</TableCell>
                <TableCell>وضعیت</TableCell>
                <TableCell>شمارش‌شده</TableCell>
                <TableCell>مغایرت</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {stocktakes.map((row) => (
                <TableRow key={pick(row, "id", "Id")}>
                  <TableCell>{pick(row, "title", "Title")}</TableCell>
                  <TableCell>{pick(row, "warehouseTitle", "WarehouseTitle")}</TableCell>
                  <TableCell>{pick(row, "statusTitle", "StatusTitle")}</TableCell>
                  <TableCell>
                    {pick(row, "countedLineCount", "CountedLineCount")}/{pick(row, "lineCount", "LineCount")}
                  </TableCell>
                  <TableCell>{pick(row, "varianceLineCount", "VarianceLineCount")}</TableCell>
                  <TableCell>
                    <Button size="small" onClick={() => openStocktake(pick(row, "id", "Id"))}>
                      باز کردن
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!stocktakes.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    انبارگردانی ثبت نشده
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={!!stocktakeDetail} onClose={() => setStocktakeDetail(null)} maxWidth="lg" fullWidth>
        <DialogTitle>{pick(stocktakeDetail, "title", "Title")}</DialogTitle>
        <DialogContent>
          {stocktakeDetail && (
            <>
              <Stack direction="row" spacing={1} mb={2}>
                <Chip label={pick(stocktakeDetail, "statusTitle", "StatusTitle")} />
                <Chip
                  label={`مغایرت: ${pick(stocktakeDetail, "varianceLineCount", "VarianceLineCount") || 0}`}
                  color="warning"
                />
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>کالا</TableCell>
                    <TableCell>سیستمی</TableCell>
                    <TableCell>شمارش</TableCell>
                    <TableCell>مغایرت</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(pick(stocktakeDetail, "lines", "Lines") || []).map((line) => {
                    const id = pick(line, "id", "Id");
                    const sys = Number(pick(line, "systemQuantity", "SystemQuantity") || 0);
                    const counted = countDraft[id] === "" ? null : Number(countDraft[id]);
                    const variance = counted == null || Number.isNaN(counted) ? null : counted - sys;
                    return (
                      <TableRow key={id}>
                        <TableCell>{pick(line, "itemTitle", "ItemTitle")}</TableCell>
                        <TableCell>{sys}</TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            type="number"
                            value={countDraft[id] ?? ""}
                            disabled={Number(pick(stocktakeDetail, "status", "Status")) !== 1}
                            onChange={(e) =>
                              setCountDraft((prev) => ({ ...prev, [id]: e.target.value }))
                            }
                            sx={{ width: 120 }}
                          />
                        </TableCell>
                        <TableCell>{variance == null ? "—" : variance}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              {Number(pick(stocktakeDetail, "status", "Status")) === 1 && (
                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={
                    <Checkbox checked={applyVariance} onChange={(e) => setApplyVariance(e.target.checked)} />
                  }
                  label="در تأیید نهایی، موجودی سیستم با شمارش اصلاح شود"
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStocktakeDetail(null)}>بستن</Button>
          {Number(pick(stocktakeDetail, "status", "Status")) === 1 && (
            <>
              <Button onClick={saveCounts}>ذخیره شمارش</Button>
              <Button variant="contained" color="success" onClick={completeStocktake}>
                تأیید انبارگردانی
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
